// Two addresses for one API: the site's own first, the worker's as a backup.
//
// 2026-09-26, over a « Connexion au serveur impossible » screen, owner:
// « handle the connexion issue, I want the website to be fast ». The page had
// loaded from app.storiz.ma and then could not reach a SECOND hostname — the
// worker's *.workers.dev — which the phone-hotspot DNS dropped again and again
// that day. The same worker now also answers at app.storiz.ma/api
// (worker/src/lib/apiPrefix.ts): nothing more to look up, and a same-origin
// call needs no CORS preflight before each authenticated request.
//
// ⚠️ THE BACKUP IS TODAY'S ADDRESS, so the worst case is exactly today.
// ⚠️ A WRITE IS NEVER SENT TWICE. A read that fails in flight is retried on
// the backup; a write is retried only when the edge refused it outright (a
// Cloudflare challenge page — it never reached the worker), because a write
// that failed in flight may already have landed.
//
// Guard: apiBase.test.ts

export interface ApiAddresses {
  primary: string;
  fallback: string | null;
}

export function apiAddresses(env: { VITE_API_URL?: string; VITE_API_SAME_ORIGIN_URL?: string }): ApiAddresses {
  const worker = env.VITE_API_URL ?? '/api';
  const same = env.VITE_API_SAME_ORIGIN_URL?.trim() || null;
  return same && same !== worker ? { primary: same, fallback: worker } : { primary: worker, fallback: null };
}

/** A Cloudflare challenge or block page instead of our JSON: the request never reached the worker. */
export function isEdgeBlock(r: Response): boolean {
  if (r.headers.get('cf-mitigated') === 'challenge') return true;
  return (r.status === 403 || r.status === 503) && (r.headers.get('content-type') ?? '').includes('text/html');
}

const READS = new Set(['GET', 'HEAD']);

export async function requestWithFallback(
  path: string,
  init: RequestInit,
  addr: ApiAddresses,
  deps: { fetch: typeof globalThis.fetch; primaryTimeoutMs?: number },
): Promise<Response> {
  if (!addr.fallback) return deps.fetch(`${addr.primary}${path}`, init);

  const isRead = READS.has((init.method ?? 'GET').toUpperCase());
  // The fast path gets its own budget for a READ only — giving up on a write
  // mid-flight could send it twice.
  const ctl = new AbortController();
  const onOuterAbort = () => ctl.abort();
  init.signal?.addEventListener('abort', onOuterAbort);
  const timer = isRead && deps.primaryTimeoutMs ? setTimeout(() => ctl.abort(), deps.primaryTimeoutMs) : null;
  try {
    const r = await deps.fetch(`${addr.primary}${path}`, { ...init, signal: ctl.signal });
    if (!isEdgeBlock(r)) return r;
  } catch (e) {
    if (init.signal?.aborted || !isRead) throw e;
  } finally {
    if (timer) clearTimeout(timer);
    init.signal?.removeEventListener('abort', onOuterAbort);
  }
  return deps.fetch(`${addr.fallback}${path}`, init);
}

export const API_ADDRESSES = apiAddresses(import.meta.env as { VITE_API_URL?: string; VITE_API_SAME_ORIGIN_URL?: string });

/** The app's API call: the fast address first, the worker's if it does not answer. */
export function apiRequest(path: string, init: RequestInit = {}, opts: { primaryTimeoutMs?: number } = {}): Promise<Response> {
  return requestWithFallback(path, init, API_ADDRESSES, { fetch: (input, i) => fetch(input, i), primaryTimeoutMs: opts.primaryTimeoutMs });
}
